import api from "../api";
import type { PaginatedResponse } from "./paginatedList";

// The envelope type lives with the list contract now; re-exported so
// existing imports keep working.
export type { PaginatedResponse } from "./paginatedList";

export type IntegrationMonitorSortField =
  | "client"
  | "name"
  | "enabled"
  | "last_run_started_at"
  | "last_run_status"
  | "next_run_at";

export type IntegrationRunSortField =
  | "started_at"
  | "finished_at"
  | "duration"
  | "trigger_type"
  | "status";

export type StepExecutionSortField =
  | "stage"
  | "stage_order"
  | "step_name"
  | "started_at"
  | "finished_at"
  | "duration"
  | "status";

export type IntegrationTriggerMode = "WEBHOOK" | "SCHEDULE";

export interface IntegrationMonitorItem {
  id: number;
  client_name: string;
  name: string;
  source_services: string[];
  target_services: string[];
  enabled: boolean;
  timezone: string;
  frequency: string | null;
  active_schedule_crons: string[];
  last_run_started_at: string | null;
  next_run_at: string | null;
  last_run_status: string | null;
  trigger_mode?: IntegrationTriggerMode;
}

export interface ScheduleItem {
  id: number;
  integration: number;
  integration_name: string;
  name: string;
  display_name: string;
  cron_expression: string;
  timezone: string;
  is_active: boolean;
  aws_schedule_arn: string;
  created_at: string;
  updated_at: string;
}

export interface IntegrationStepItem {
  id: number;
  integration: number;
  stage: string;
  stage_order: number;
  step_key: string;
  name: string;
  is_enabled: boolean;
  connection: number | null;
  config: Record<string, unknown>;
}

export interface LastRunSummary {
  id: number;
  status: string;
  trigger_type: string;
  started_at: string;
  finished_at: string | null;
}

export interface ConfigProfileSummary {
  id: number;
  name: string;
  config_schema_key: string;
  used_by: number;
}

export interface IntegrationDetail {
  id: number;
  client: number;
  client_name: string;
  name: string;
  slug: string;
  description: string;
  execution_target: string;
  timezone: string;
  is_active: boolean;
  notifications_enabled: boolean;
  timezone_abbreviation: string;
  created_at: string;
  updated_at: string;
  config: Record<string, unknown>;
  config_profile?: number | null;
  config_profile_detail?: ConfigProfileSummary | null;
  effective_config?: Record<string, unknown>;
  steps: IntegrationStepItem[];
  schedules: ScheduleItem[];
  source_services: string[];
  target_services: string[];
  last_run: LastRunSummary | null;
  next_run_at: string | null;
  frequency: string | null;
  trigger_mode?: IntegrationTriggerMode;
}

export interface IntegrationRunListItem {
  id: number;
  integration: number;
  integration_name: string;
  client_name: string;
  execution_id: string;
  trigger_type: string;
  execution_target: string;
  status: string;
  worker_id: string;
  started_at: string;
  finished_at: string | null;
  error_message: string;
  error_detail?: string | null;
  /** CloudWatch Insights link to this run's log lines; "" when not derivable. */
  logs_url: string;
}

export interface StepExecutionItem {
  id: number;
  step: number;
  step_name: string;
  step_key: string;
  stage: string;
  stage_order: number;
  status: string;
  started_at: string;
  finished_at: string | null;
  details: Record<string, unknown>;
}

export interface IntegrationRunDetail extends IntegrationRunListItem {
  metadata: Record<string, unknown>;
  step_executions: StepExecutionItem[];
}

export interface IntegrationFolder {
  id: number;
  folder: string;
  folder_prefix: string;
  s3_bucket: string;
  sync_status: string;
}

export interface StepConnectionRequirement {
  connection_type: string; // Connection.Type value, e.g. "API" | "SFTP"
  provider_slug: string | null; // null = any provider of that type
}

export interface StepCatalogEntry {
  step_key: string;
  class_name: string;
  config_schema: Record<string, unknown> | null;
  // null = the step takes no connection; undefined = old backend (unknown).
  connection_requirement?: StepConnectionRequirement | null;
  requires_event_payload?: boolean;
  suggested_stage?: string | null;
}

export type ReadinessSeverity = "error" | "warning";

export type ReadinessScope =
  | "integration"
  | "step"
  | "connection"
  | "webhook"
  | "schedule";

/**
 * Issue codes emitted by the readiness endpoint - mirrors the backend
 * ReadinessCode + RequirementViolationCode enums
 * (backend/integration_platform/api/integrations/readiness.py).
 */
export type ReadinessCode =
  | "no_backend_user"
  | "config_profile_schema_key_mismatch"
  | "config_schema_key_unknown"
  | "integration_config_invalid"
  | "no_active_webhook_trigger"
  | "mode_conflict_schedules"
  | "no_active_schedule"
  | "mode_conflict_webhook_triggers"
  | "unknown_step_key"
  | "connection_missing"
  | "no_adapter_registered"
  | "connection_config_invalid"
  | "missing_required_credential"
  | "credential_secret_id_empty"
  | "webhook_connection_client_mismatch"
  | "webhook_provider_mismatch"
  | "webhook_secret_missing"
  | "connection_not_allowed"
  | "connection_type_mismatch"
  | "connection_provider_mismatch"
  | "connection_client_mismatch";

export interface ReadinessIssue {
  severity: ReadinessSeverity;
  scope: ReadinessScope;
  code: ReadinessCode;
  message: string;
  step_id: number | null;
  connection_id: number | null;
}

export interface IntegrationReadiness {
  integration: number;
  trigger_mode: IntegrationTriggerMode;
  ready: boolean;
  issues: ReadinessIssue[];
}

export interface TriggerRunResponse {
  run_id: string;
  status: string;
  task_arn?: string;
}

export interface GetIntegrationMonitorParams {
  page?: number;
  page_size?: number;
  search?: string;
  enabled?: "true" | "false";
  sort_by?: IntegrationMonitorSortField;
  sort_order?: "asc" | "desc";
  last_run_after?: string;
  last_run_before?: string;
  next_run_after?: string;
  next_run_before?: string;
  client?: string;
  source_type?: string;
  target_type?: string;
  last_status?: string;
}

export interface GetIntegrationRunsParams {
  integration_id: number;
  page?: number;
  page_size?: number;
  sort_by?: IntegrationRunSortField;
  sort_order?: "asc" | "desc";
  status?: string;
  trigger_type?: string;
  started_after?: string;
  started_before?: string;
  finished_after?: string;
  finished_before?: string;
}

// --- Deletion plan (GET .../deletion-preview/ and the DELETE response)
// Mirrors integration_platform/api/integrations/deletion.py. Read defensively
// at the call sites (`?? []`, `?? 0`): a field rename on the backend should
// degrade to a missing bullet, never to a crash mid-confirmation.

/** A run still PENDING/RUNNING - blocks a non-forced delete. */
export interface DeletionInFlightRun {
  id: number;
  execution_id: string;
  status: string;
  trigger_type: string;
  started_at: string;
  age_minutes: number;
  /** Likely dead without having updated its row (no stale-run reaper). */
  is_stale: boolean;
  /** ECS task ARN - the platform cannot stop it, the operator must. */
  worker_id: string;
}

export interface DeletionBlocker {
  code: string;
  message: string;
  overridable: boolean;
  runs: DeletionInFlightRun[];
}

export interface DeletionWarning {
  code: string;
  message: string;
}

export interface DeletionPlannedSchedule {
  id: number;
  display_name: string;
  /** EventBridge Scheduler schedule name, deleted in AWS too. */
  aws_schedule_name: string;
  aws_managed: boolean;
  cron_expression: string;
  timezone: string;
  is_active: boolean;
}

export interface DeletionPlannedWebhookTrigger {
  id: number;
  name: string;
  provider: string;
  connection_id: number;
  connection_name: string;
  /** The trigger's dedicated WEBHOOK connection goes with it. */
  connection_will_be_deleted: boolean;
  credential_types: string[];
}

export interface DeletionPlannedStorageFolder {
  folder_ids: number[];
  folder_prefix: string;
  s3_bucket: string;
  display_name: string;
  file_rows: number;
  will_be_removed: boolean;
  skip_reason: string | null;
  shared_with: string[];
}

export interface DeletionCounted {
  count: number;
  names: string[];
  truncated: boolean;
}

export interface DeletionRetainedConnection {
  id: number;
  name: string;
  provider: string;
  type: string;
  reason: string;
  used_by_other_integrations: number;
  other_integration_names: string[];
  credential_types: string[];
}

export interface DeletionPlan {
  mode: "planned" | "executed";
  integration: {
    id: number;
    name: string;
    slug: string;
    client_name: string;
    is_active: boolean;
    storage_folder: string;
  };
  computed_at: string;
  can_delete: boolean;
  force_supported: boolean;
  blockers: DeletionBlocker[];
  removals: {
    steps: DeletionCounted;
    schedules: DeletionPlannedSchedule[];
    webhook_triggers: DeletionPlannedWebhookTrigger[];
    runs: DeletionCounted;
    runs_by_status: Record<string, number>;
    step_executions: DeletionCounted;
    processed_sftp_files: DeletionCounted;
    storage_folder: DeletionPlannedStorageFolder | null;
  };
  retentions: {
    s3_objects: { s3_bucket: string; prefix: string; note: string };
    connections: DeletionRetainedConnection[];
    config_profile: { id: number; name: string; used_by: number } | null;
    processed_webhook_events: DeletionCounted;
    cloudwatch_logs_note: string;
  };
  warnings: DeletionWarning[];
  forced: boolean;
  raced_runs: DeletionInFlightRun[];
}

const monitorTag = (id: number | "LIST") => ({
  type: "IntegrationMonitor" as const,
  id,
});
const runsTag = (id: number | "LIST") => ({
  type: "IntegrationRuns" as const,
  id,
});
// Connection/credential mutations don't know the affected integration id,
// so they invalidate the "ALL" tag; readiness is cheap to refetch.
export const readinessTag = (id: number | "ALL") => ({
  type: "IntegrationReadiness" as const,
  id,
});
// Defined here (not in configProfiles.ts) so both files can import it
// without a circular dependency - same pattern as readinessTag.
export const profileTag = (id: number | "LIST") => ({
  type: "IntegrationConfigProfile" as const,
  id,
});

export const integrationsBetaApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getIntegrationMonitorList: builder.query<
      PaginatedResponse<IntegrationMonitorItem>,
      GetIntegrationMonitorParams
    >({
      query: (params) => ({
        url: "/integration-platform/integrations/monitor/",
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((row) => monitorTag(row.id)),
              monitorTag("LIST"),
            ]
          : [monitorTag("LIST")],
    }),

    getIntegrationDetail: builder.query<IntegrationDetail, number>({
      query: (id) => ({
        url: `/integration-platform/integrations/${id}/`,
        method: "GET",
      }),
      // Linked details also carry the profile tag so editing a shared config
      // profile refetches every cached integration that uses it.
      providesTags: (result, _err, id) =>
        result?.config_profile != null
          ? [monitorTag(id), profileTag(result.config_profile)]
          : [monitorTag(id)],
    }),

    getIntegrationRuns: builder.query<
      PaginatedResponse<IntegrationRunListItem>,
      GetIntegrationRunsParams
    >({
      query: ({ integration_id, sort_by, sort_order, ...rest }) => ({
        url: "/integration-platform/runs/",
        method: "GET",
        params: {
          integration_id,
          ...rest,
          ...(sort_by ? { sort_by, sort_order } : {}),
        },
      }),
      providesTags: (_result, _err, { integration_id }) => [
        runsTag(integration_id),
      ],
    }),

    getIntegrationRunDetail: builder.query<IntegrationRunDetail, number>({
      query: (runId) => ({
        url: `/integration-platform/runs/${runId}/`,
        method: "GET",
      }),
    }),

    updateIntegration: builder.mutation<
      IntegrationDetail,
      {
        id: number;
        name?: string;
        description?: string;
        timezone?: string;
        execution_target?: string;
        notifications_enabled?: boolean;
        config?: Record<string, unknown>;
        config_profile?: number | null;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/integration-platform/integrations/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, arg) => [
        monitorTag(arg.id),
        monitorTag("LIST"),
        readinessTag(arg.id),
        // Link/unlink changes used_by on both the old and new profile; the
        // old id is unknown here, so invalidate the whole tag type.
        ...("config_profile" in arg
          ? [{ type: "IntegrationConfigProfile" as const }]
          : []),
      ],
    }),

    updateStep: builder.mutation<
      IntegrationStepItem,
      {
        integrationId: number;
        id: number;
        config?: Record<string, unknown>;
        is_enabled?: boolean;
        connection?: number | null;
        name?: string;
        stage?: string;
        stage_order?: number;
      }
    >({
      query: ({ integrationId, id, ...body }) => ({
        url: `/integration-platform/integrations/${integrationId}/steps/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, { integrationId }) => [
        monitorTag(integrationId),
        readinessTag(integrationId),
      ],
    }),

    deleteStep: builder.mutation<
      unknown,
      { integrationId: number; id: number }
    >({
      query: ({ integrationId, id }) => ({
        url: `/integration-platform/integrations/${integrationId}/steps/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { integrationId }) => [
        monitorTag(integrationId),
        monitorTag("LIST"),
        readinessTag(integrationId),
      ],
    }),

    getIntegrationReadiness: builder.query<IntegrationReadiness, number>({
      query: (id) => ({
        url: `/integration-platform/integrations/${id}/readiness/`,
        method: "GET",
      }),
      providesTags: (_result, _err, id) => [
        readinessTag(id),
        readinessTag("ALL"),
      ],
    }),

    toggleIntegration: builder.mutation<
      { id: number; is_active: boolean },
      { id: number; is_active: boolean }
    >({
      query: ({ id, is_active }) => ({
        url: `/integration-platform/integrations/${id}/toggle/`,
        method: "PATCH",
        body: { is_active },
      }),
      // Optimistic: flip the integration immediately and cascade every
      // schedule switch (the backend toggles all schedules with it). Revert
      // on failure; the alert is raised by the caller. Endpoints are injected
      // across files, so the base api can't statically type them — cast the
      // patcher (the cache is shared, so it works at runtime).
      async onQueryStarted({ id, is_active }, { dispatch, queryFulfilled }) {
        const upd = api.util.updateQueryData as unknown as <T>(
          endpoint: string,
          arg: unknown,
          updater: (draft: T) => void,
        ) => unknown;
        const patches: { undo: () => void }[] = [
          dispatch(
            upd<IntegrationDetail>("getIntegrationDetail", id, (draft) => {
              draft.is_active = is_active;
              draft.schedules.forEach((s) => {
                s.is_active = is_active;
              });
            }) as never,
          ) as { undo: () => void },
          dispatch(
            upd<ScheduleItem[]>("getSchedules", id, (draft) => {
              draft.forEach((s) => {
                s.is_active = is_active;
              });
            }) as never,
          ) as { undo: () => void },
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: (_result, _err, { id }) => [
        monitorTag(id),
        monitorTag("LIST"),
        readinessTag(id),
      ],
    }),

    triggerIntegrationRun: builder.mutation<
      TriggerRunResponse,
      { integrationId: number; event_payload?: Record<string, unknown> }
    >({
      query: ({ integrationId, event_payload }) => ({
        url: `/integration-platform/integrations/${integrationId}/run/`,
        method: "POST",
        body: { event_payload: event_payload ?? {} },
      }),
      invalidatesTags: (_result, _err, { integrationId }) => [
        runsTag(integrationId),
        monitorTag(integrationId),
      ],
    }),

    getIntegrationFolder: builder.query<IntegrationFolder, number>({
      query: (integrationId) => ({
        url: `/integration-platform/integrations/${integrationId}/folder/`,
        method: "GET",
      }),
    }),

    getStepCatalog: builder.query<StepCatalogEntry[], void>({
      query: () => ({
        url: "/integration-platform/integrations/steps/catalog/",
        method: "GET",
      }),
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    getIntegrationConfigSchema: builder.query<
      {
        config_schema_key: string;
        config_schema: Record<string, unknown> | null;
      },
      number
    >({
      query: (id) => ({
        url: `/integration-platform/integrations/${id}/config-schema/`,
        method: "GET",
      }),
    }),

    getIntegrationDeletionPreview: builder.query<DeletionPlan, number>({
      query: (id) => ({
        url: `/integration-platform/integrations/${id}/deletion-preview/`,
        method: "GET",
      }),
      // Untagged and never cached: the blocker list is time-sensitive (a run
      // can start at any moment) so the dialog must not show a stale report,
      // and once the integration is gone the report is meaningless. The dialog
      // refetches explicitly instead.
      keepUnusedDataFor: 0,
    }),

    deleteIntegration: builder.mutation<
      DeletionPlan,
      { id: number; force?: boolean }
    >({
      query: ({ id, force }) => ({
        url: `/integration-platform/integrations/${id}/`,
        method: "DELETE",
        ...(force ? { params: { force: "true" } } : {}),
      }),
      // Only tags that other, still-living caches provide. Deliberately NOT
      // monitorTag(id) / readinessTag / runsTag(id) / IntegrationSchedule /
      // WebhookTrigger: those are provided by the deleted integration's own
      // queries, which are still subscribed while its page unmounts, so
      // invalidating them just fires 404 refetches for a row that is gone.
      invalidatesTags: () => [
        monitorTag("LIST"),
        // The shared config profile's used_by count drops by one.
        profileTag("LIST"),
        // Dedicated webhook connections were deleted with their triggers.
        { type: "IntegrationConnection" as const },
        // The Data-Storage folder vanished; its cache is keyed by S3 prefix,
        // which cannot be mapped to a tag id here.
        { type: "FileSystem" as const },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetIntegrationMonitorListQuery,
  useGetIntegrationDetailQuery,
  useGetIntegrationRunsQuery,
  useGetIntegrationRunDetailQuery,
  useUpdateIntegrationMutation,
  useUpdateStepMutation,
  useDeleteStepMutation,
  useGetIntegrationReadinessQuery,
  useToggleIntegrationMutation,
  useTriggerIntegrationRunMutation,
  useGetIntegrationFolderQuery,
  useGetStepCatalogQuery,
  useGetIntegrationConfigSchemaQuery,
  useGetIntegrationDeletionPreviewQuery,
  useDeleteIntegrationMutation,
} = integrationsBetaApi;
